/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { SupersetClient } from '@superset-ui/core';
import { RELATIONSHIP_MODEL_VERSION } from '../constants';
import {
  migrateVqbNodeInfoToModel,
  wrapModelInEnvelope,
} from '../migrate/vqbToModel';
import type { PersistedModelEnvelope, RelationshipModel } from '../types';

export interface ModelUiPrefs {
  autoExecute?: boolean;
  sqlPreviewCollapsed?: boolean;
  viewport?: { x: number; y: number; zoom: number };
  workbenchContext?: import('../filterBridge/types').WorkbenchContext;
}

export function serializeModel(
  model: RelationshipModel,
  uiPrefs?: ModelUiPrefs,
): string {
  const envelope = wrapModelInEnvelope(model, uiPrefs);
  return JSON.stringify(envelope);
}

export function deserializeModel(
  json: string | unknown,
): RelationshipModel | null {
  if (!json) {
    return null;
  }
  try {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    return migrateVqbNodeInfoToModel(parsed);
  } catch {
    return null;
  }
}

export function buildNodeInfoEnvelope(
  model: RelationshipModel,
  uiPrefs?: ModelUiPrefs,
): PersistedModelEnvelope {
  return wrapModelInEnvelope(model, uiPrefs);
}

export function parseNodeInfo(nodeInfo: unknown): {
  model: RelationshipModel | null;
  uiPrefs?: ModelUiPrefs;
} {
  if (!nodeInfo || typeof nodeInfo !== 'object') {
    return { model: null };
  }

  const envelope = nodeInfo as PersistedModelEnvelope;
  if (
    envelope.version === RELATIONSHIP_MODEL_VERSION &&
    envelope.relationshipModel
  ) {
    return { model: envelope.relationshipModel, uiPrefs: envelope.uiPrefs };
  }

  const model = migrateVqbNodeInfoToModel(nodeInfo);
  if (!model) {
    return { model: null };
  }

  const legacy = nodeInfo as {
    viewport?: ModelUiPrefs['viewport'];
    autoExecute?: boolean;
    sqlPreviewCollapsed?: boolean;
  };

  return {
    model,
    uiPrefs: {
      viewport: legacy.viewport,
      autoExecute: legacy.autoExecute,
      sqlPreviewCollapsed: legacy.sqlPreviewCollapsed,
    },
  };
}

export async function saveModelToTabState(
  queryEditorId: string,
  model: RelationshipModel,
  uiPrefs?: ModelUiPrefs,
): Promise<void> {
  const nodeInfo = serializeModel(model, uiPrefs);
  await SupersetClient.put({
    endpoint: encodeURI(`/tabstateview/${queryEditorId}`),
    postPayload: { node_info: nodeInfo },
  });
}

export async function loadModelFromTabState(
  queryEditorId: string,
): Promise<{ model: RelationshipModel | null; uiPrefs?: ModelUiPrefs }> {
  const { json } = await SupersetClient.get({
    endpoint: `/tabstateview/${queryEditorId}`,
  });
  const tabState = json as { node_info?: unknown };
  if (!tabState?.node_info) {
    return { model: null };
  }
  return parseNodeInfo(tabState.node_info);
}
